import { BehaviorSubject, fromEvent } from "rxjs";
import { FormId } from "../../login/src/common/formId";
import { getComponentElement } from "./baseComponent.helpers";
import { CdpaTooltip } from "./cdpaTooltip.component";
import { HTMLElementAttribute, HTMLElementEventType } from "./misc.enums";
import { SlugId, Slugs } from "./slug.loader";
import { ValidityState } from "./validators/baseValidator";
import { getEmailValidityStateAsync } from "./validators/emailValidator";
import { getRegisterUsernameValidityState } from "./validators/usernameValidator";
import { UIType } from "./cdpaRequest";
import { sendInteractionMetrics } from "../../login/src/formHelpers/metricsHelper";
export var CdpaInputId;
(function (CdpaInputId) {
    CdpaInputId["Email"] = "email";
    CdpaInputId["Username"] = "username";
    CdpaInputId["RegisterUsername"] = "register-username";
})(CdpaInputId || (CdpaInputId = {}));
var CdpaInputQuerySelector;
(function (CdpaInputQuerySelector) {
    CdpaInputQuerySelector["Base"] = ".cdpa-input";
    CdpaInputQuerySelector["Icon"] = ".cdpa-input__icon";
    CdpaInputQuerySelector["Label"] = ".cdpa-input__label";
    CdpaInputQuerySelector["ErrorLabel"] = ".cdpa-input__error-label";
    CdpaInputQuerySelector["CustomSignUpLabel"] = ".cdpa-input__sign-up-label";
    CdpaInputQuerySelector["CustomLoginLabel"] = ".cdpa-input__login-label";
    CdpaInputQuerySelector["Tooltip"] = ".cdpa-tooltip";
})(CdpaInputQuerySelector || (CdpaInputQuerySelector = {}));
var CdpaInputClassModifier;
(function (CdpaInputClassModifier) {
    CdpaInputClassModifier["Valid"] = "cdpa-input--valid";
    CdpaInputClassModifier["Invalid"] = "cdpa-input--invalid";
    CdpaInputClassModifier["Info"] = "cdpa-input--info";
})(CdpaInputClassModifier || (CdpaInputClassModifier = {}));
const validatorsMap = {
    [CdpaInputId.Email]: getEmailValidityStateAsync,
    [CdpaInputId.RegisterUsername]: getRegisterUsernameValidityState,
    [CdpaInputId.Username]: getEmailValidityStateAsync
};
export class CdpaInputComponent {
    constructor(elementId) {
        this.elementId = elementId;
        this.labelText = null;
        this.errorLabelText = null;
        this.validationInProgress = null;
        this.isDirty = true;
        this.validityStateSubject = new BehaviorSubject(ValidityState.Indeterminate);
        this.validityState$ = this.validityStateSubject.asObservable();
        this.inputElement = getComponentElement(document.getElementById(elementId));
        this.checkAutofocus();
        this.parentElement = getComponentElement(this.inputElement.closest(CdpaInputQuerySelector.Base));
        this.parentForm = getComponentElement(this.inputElement.closest("form"));
        this.inputIcon = getComponentElement(this.parentElement.querySelector(CdpaInputQuerySelector.Icon));
        this.inputLabel = getComponentElement(this.parentElement.querySelector(CdpaInputQuerySelector.Label));
        this.inputErrorLabel = getComponentElement(this.parentElement.querySelector(CdpaInputQuerySelector.ErrorLabel));
        this.customSignUpErrorLabel = getComponentElement(this.parentElement.querySelector(CdpaInputQuerySelector.CustomSignUpLabel));
        this.customLoginErrorLabel = getComponentElement(this.parentElement.querySelector(CdpaInputQuerySelector.CustomLoginLabel));
        new CdpaTooltip(this.inputIcon);
        this.initRegisterUsernameLabel();
        this.inputElement.addEventListener(HTMLElementEventType.Focus, () => {
            sendInteractionMetrics(this.inputElement, UIType.Input);
        });
        this.inputElement.addEventListener(HTMLElementEventType.Input, () => {
            this.resetValidityState();
            this.isDirty = true;
        });
        this.inputElement.addEventListener(HTMLElementEventType.Change, () => {
            this.inputElement.setAttribute(HTMLElementAttribute.Value, this.inputElement.value);
        });
        if (this.hasBackendError()) {
            // If the backend error is one of custom ones, show custom message
            if (this.hasCustomError()) {
                this.showCustomError();
                return;
            }
            this.showBackendError();
        }
        else if (this.value.startsWith("##")) {
            // when form times out after registration it redirects to login with ## in email field
            this.value = "";
        }
        else if (this.value !== "") {
            // If field is prefilled initially, validate it
            this.validate();
        }
        this.subscribeValidators();
        this.validityStateSubject.subscribe((value) => {
            this.updateValidityClass(value);
        });
    }
    set isHidden(value) {
        if (value) {
            this.parentElement.setAttribute("hidden", "");
        }
        else {
            this.parentElement.removeAttribute("hidden");
        }
    }
    get value() {
        return this.inputElement.value;
    }
    set value(value) {
        this.inputElement.value = value;
        // without this, value attribute is not updated ¯\_(ツ)_/¯
        this.inputElement.setAttribute("value", value);
    }
    resetValidityState() {
        this.setValidityState(ValidityState.Indeterminate);
        CdpaInputComponent.hideCustomLabelError(this.customSignUpErrorLabel);
        CdpaInputComponent.hideCustomLabelError(this.customLoginErrorLabel);
    }
    async validate() {
        if (this.isDirty === false) {
            return this.isValid();
        }
        const validator = validatorsMap[this.elementId];
        if (validator === null) {
            return true;
        }
        // If there is a validation already in progress, wait for it to finish
        if (this.validationInProgress !== null) {
            await this.validationInProgress;
        }
        this.validationInProgress = validator(this.value);
        const { valid, reason } = await this.validationInProgress;
        this.setValidityState(valid, reason ?? null);
        this.validationInProgress = null;
        return this.isValid();
    }
    async checkForBackendValidated() {
        if (this.validationInProgress !== null) {
            await this.validationInProgress;
        }
        // If there is no validation error from backend, mark the field as backend validated
        if (this.hasBackendError()) {
            return;
        }
        this.setValidityState(ValidityState.BackendValid);
    }
    showCustomError() {
        if (this.hasInvalidEmail()) {
            CdpaInputComponent.showCustomLabelError(this.customSignUpErrorLabel);
        }
        else if (this.hasExistingEmail()) {
            if (this.parentForm.id === FormId.UpdateEmailForm) {
                return this.showBackendError();
            }
            CdpaInputComponent.showCustomLabelError(this.customLoginErrorLabel);
        }
        this.setValidityState(ValidityState.Info);
    }
    hasCustomError() {
        return this.hasInvalidEmail() || this.hasExistingEmail();
    }
    hasInvalidEmail() {
        return this.getDataLabel() === Slugs.translated[SlugId.InvalidUsernameOrEmailMessage];
    }
    hasExistingEmail() {
        return this.getDataLabel() === Slugs.translated[SlugId.EmailExistsMessage];
    }
    showBackendError() {
        this.setValidityState(ValidityState.Invalid, this.getDataLabel());
    }
    hasBackendError() {
        return this.parentElement.hasAttribute(HTMLElementAttribute.DataLabel);
    }
    getDataLabel() {
        return this.parentElement.getAttribute(HTMLElementAttribute.DataLabel);
    }
    isValid() {
        return [ValidityState.FrontendValid, ValidityState.BackendValid].includes(this.validityStateSubject.getValue());
    }
    initRegisterUsernameLabel() {
        if (this.elementId === CdpaInputId.RegisterUsername) {
            this.setLabel(Slugs.translated.usernameHint);
        }
    }
    subscribeValidators() {
        fromEvent(this.inputElement, HTMLElementEventType.Blur).subscribe(() => {
            if (this.value === "") {
                return;
            }
            this.validate();
        });
    }
    setValidityState(value, reason = null) {
        this.validityStateSubject.next(value);
        this.isDirty = false;
        this.updateValidityClass(value);
        this.setErrorLabel(reason);
    }
    setLabel(label) {
        this.labelText = label;
        this.adjustLabelVisibility();
    }
    setErrorLabel(label) {
        this.errorLabelText = label;
        this.adjustErrorLabelVisibility();
    }
    updateValidityClass(validityState) {
        if (validityState === ValidityState.Info) {
            this.parentElement.classList.add(CdpaInputClassModifier.Info);
        }
        else {
            this.parentElement.classList.remove(CdpaInputClassModifier.Info);
        }
        if (validityState === ValidityState.Invalid) {
            this.parentElement.classList.add(CdpaInputClassModifier.Invalid);
        }
        else {
            this.parentElement.classList.remove(CdpaInputClassModifier.Invalid);
        }
        // Mark the field as valid only if it has been validated by backend
        if (validityState === ValidityState.BackendValid) {
            this.parentElement.classList.add(CdpaInputClassModifier.Valid);
        }
        else {
            this.parentElement.classList.remove(CdpaInputClassModifier.Valid);
        }
    }
    adjustLabelVisibility() {
        const cdpaInputLabelHidden = "cdpa-input__label--hidden";
        CdpaInputComponent.adjustAnyLabelVisibility(this.labelText, this.inputLabel, cdpaInputLabelHidden);
    }
    adjustErrorLabelVisibility() {
        const cdpaInputErrorLabelHidden = "cdpa-input__error-label--hidden";
        CdpaInputComponent.adjustAnyLabelVisibility(this.errorLabelText, this.inputErrorLabel, cdpaInputErrorLabelHidden);
    }
    checkAutofocus() {
        const hasAutofocus = this.inputElement.getAttribute(HTMLElementAttribute.DataAutofocus);
        const isMobile = navigator.userAgent.includes("Mobile");
        if (hasAutofocus && !isMobile) {
            this.inputElement.focus();
        }
    }
    static adjustAnyLabelVisibility(labelText, labelElement, hiddenClass) {
        if (!labelText) {
            // Don't remove the label text from html - we need it there for the label hiding animation to work correctly
            labelElement.classList.add(hiddenClass);
        }
        else {
            labelElement.innerHTML = labelText;
            labelElement.classList.remove(hiddenClass);
        }
    }
    static showCustomLabelError(labelElement) {
        labelElement.classList.remove("cdpa-input__custom-label--hidden");
        const aElement = labelElement.getElementsByTagName("a").item(0);
        if (aElement !== null) {
            aElement.tabIndex = 0;
        }
    }
    static hideCustomLabelError(labelElement) {
        labelElement.classList.add("cdpa-input__custom-label--hidden");
        const aElement = labelElement.getElementsByTagName("a").item(0);
        if (aElement !== null) {
            aElement.tabIndex = -1;
        }
    }
}
